import { cn } from '@/lib/utils';

type WaterfallStepKind = 'base' | 'increase' | 'decrease' | 'total';

export interface WaterfallStep {
  key: string;
  label: string;
  kind: WaterfallStepKind;
  valueLabel: string;
  deltaValue: number;
}

export interface WaterfallChartProps {
  steps: WaterfallStep[];
  className?: string;
}

const toneByKind: Record<WaterfallStepKind, string> = {
  base: 'bg-emerald-500/80 dark:bg-emerald-400/70',
  increase: 'bg-emerald-500 dark:bg-emerald-400',
  decrease: 'bg-slate-500 dark:bg-slate-400',
  total: 'bg-sky-500 dark:bg-sky-400',
};

export const WaterfallChart = ({ steps, className }: WaterfallChartProps): JSX.Element => {
  const maxAbs = steps.reduce((carry, step) => {
    if (step.kind === 'increase' || step.kind === 'decrease') {
      return Math.max(carry, Math.abs(step.deltaValue));
    }
    return carry;
  }, 0);

  return (
    <div className={cn('space-y-3', className)}>
      {steps.map((step) => {
        const isDelta = step.kind === 'increase' || step.kind === 'decrease';
        const width = maxAbs > 0 ? Math.max(6, (Math.abs(step.deltaValue) / maxAbs) * 100) : 0;
        const alignmentClass = step.kind === 'decrease' ? 'right-0' : 'left-0';
        return (
          <div key={step.key} className="space-y-1">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm font-medium text-foreground">{step.label}</p>
              <span className={cn('text-sm font-semibold', step.kind === 'decrease' ? 'text-foreground/80' : 'text-foreground')}>
                {step.valueLabel}
              </span>
            </div>
            {isDelta ? (
              <div className="relative h-3 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className={cn('absolute top-0 h-full rounded-full', toneByKind[step.kind], alignmentClass)}
                  style={{ width: `${width}%` }}
                  aria-hidden="true"
                />
              </div>
            ) : (
              <div
                className={cn(
                  'h-3 w-full rounded-full',
                  step.kind === 'base' ? toneByKind.base : toneByKind.total,
                  step.kind === 'base' ? 'opacity-80' : '',
                )}
                aria-hidden="true"
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
